import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import axiosInstance from "../../shared/axiosInstance";
import { API, INTERNAL_REJECTION_REASON_TYPE } from "../../constants";
import {
  IInvoiceListItem,
  IInvoiceRaw,
  IRejectionReasonsResponse,
} from "./invoices.api.types";
import { mapInvoiceListItem, selectInvoiceList } from "./invoices.mapper";
import { buildInvoiceListUrl } from "./invoices.scope";

export const invoiceKeys = {
  all: ["invoices"] as const,
  lists: () => [...invoiceKeys.all, "list"] as const,
  list: (url: string) => [...invoiceKeys.lists(), url] as const,
  header: (id: string) => [...invoiceKeys.all, "header", id] as const,
  vendorInvoiceNo: (id: string) => ["invoiceVendorInvoiceNo", id] as const,
  rejectionReasons: () => ["invoiceRejectionReasons"] as const,
};

export const useVendorInvoiceNoBridge = (id: string | undefined) =>
  useQuery<string | null>({
    queryKey: invoiceKeys.vendorInvoiceNo(id ?? ""),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  }).data ?? null;

export const fetchInvoiceList = async (url: string) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}/${url}`);
  return res.data;
};

export const useInvoiceListQuery = (url?: string) =>
  useQuery({
    queryKey: invoiceKeys.list(url ?? ""),
    queryFn: () => fetchInvoiceList(url as string),
    enabled: !!url,
  });

export const useFindInvoiceByNumber = (userInfo: any) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      invoiceNumber: string,
      preferStatuses: string[] = []
    ): Promise<IInvoiceListItem | null> => {
      const url = buildInvoiceListUrl(
        API.INVOICE.resolveListWindow(API.INVOICE.LIST_WINDOW_DAYS_MAX),
        userInfo
      );
      const res = await queryClient.fetchQuery({
        queryKey: invoiceKeys.list(url),
        queryFn: () => fetchInvoiceList(url),
        staleTime: 60_000,
      });
      const matches = selectInvoiceList(res)
        .map(mapInvoiceListItem)
        .filter((inv) => inv.invoiceNumber === invoiceNumber);
      return (
        matches.find((inv) => preferStatuses.includes(inv.status)) ??
        matches[0] ??
        null
      );
    },
    [queryClient, userInfo]
  );
};

export const fetchRejectionReasons = async () => {
  const res = await axiosInstance.get(
    `${process.env.REACT_APP_BASE_URL}/${API.INVOICE.REJECTION_REASONS}`
  );
  return res.data as IRejectionReasonsResponse;
};

export const useRejectionReasonsQuery = (enabled: boolean) =>
  useQuery({
    queryKey: invoiceKeys.rejectionReasons(),
    queryFn: fetchRejectionReasons,
    enabled,
    staleTime: Infinity,
    select: (res) =>
      (res?.data ?? [])
        .filter((r) => r.type !== INTERNAL_REJECTION_REASON_TYPE)
        .map((r) => ({ label: r.description, value: r.id })),
  });

const selectInternalReasonCodes = (res: IRejectionReasonsResponse) =>
  (res?.data ?? [])
    .filter((r) => r.type === INTERNAL_REJECTION_REASON_TYPE)
    .map((r) => r.id);

export const useInternalRejectionReasonCodes = (enabled: boolean) =>
  useQuery({
    queryKey: invoiceKeys.rejectionReasons(),
    queryFn: fetchRejectionReasons,
    enabled,
    staleTime: Infinity,
    select: selectInternalReasonCodes,
  });

const findRow = (res: unknown, id: string): IInvoiceRaw | null =>
  selectInvoiceList(res).find((inv) => String(inv.invoice_id) === String(id)) ??
  null;

const findCachedRow = (client: QueryClient, id: string) => {
  for (const q of client.getQueryCache().findAll({ queryKey: invoiceKeys.lists() })) {
    const row = findRow(q.state.data, id);
    if (row) return { row, updatedAt: q.state.dataUpdatedAt };
  }
  return undefined;
};

export const useInvoiceHeaderQuery = (
  id: string | undefined,
  listUrl: string | undefined,
  enabled: boolean
) => {
  const queryClient = useQueryClient();
  const safeId = id ?? "";
  const seed = useMemo(
    () => findCachedRow(queryClient, safeId),
    [queryClient, safeId]
  );

  return useQuery({
    queryKey: invoiceKeys.header(safeId),
    queryFn: async () => {
      const res = await queryClient.fetchQuery({
        queryKey: invoiceKeys.list(listUrl as string),
        queryFn: () => fetchInvoiceList(listUrl as string),
      });
      return findRow(res, safeId);
    },
    initialData: () => seed?.row,
    initialDataUpdatedAt: () => seed?.updatedAt,
    enabled: enabled && !!id && !!listUrl,
  });
};
